import { Button, Slider } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { useUpdatePicturesMutation } from 'types/atlas-graphql';
import { EDITOR } from 'utils/access-control/roles';
import { usePermissions } from 'utils/usePermissions';

import { LevelsButtonContainer, LevelsContainer } from './Controls.style';
import { type FiltersProps } from './Filters';

const toValue = ({ a = 1, b = 0 } = {}): [number, number] => {
  const min = -b / a;
  const max = (255 - b) / a;

  return [min, max];
};

export const Levels: React.FunctionComponent<
  FiltersProps & {
    menuOpen: boolean;
    onLevelsToggle: () => void;
    onLevelsChange?: (value: boolean) => void;
  }
> = ({
  levels: defaultValue,
  pictureId,
  pictureGlobalId,
  refetch,
  onChange,
  onLevelsToggle,
  onLevelsChange,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { hasRole } = usePermissions();
  const isEditor = hasRole(EDITOR);

  const { a, b } = defaultValue ?? { a: 1, b: 0 };
  const [value, setValue] = useState<[number, number]>(toValue(defaultValue));
  const [levels, setLevels] = useState<{ a?: number; b?: number }>({ a, b });
  const [saving, setSaving] = useState<boolean>(false);

  const hasDefaultLevels = useMemo(() => a !== 1 || b !== 0, [a, b]);
  const isEdited = useMemo(() => {
    const [defaultLevelsA, defaultLevelsB] = [defaultValue?.a ?? 1, defaultValue?.b ?? 0];
    const [currentLevelsA, currentLevelsB] = [levels?.a ?? 1, levels?.b ?? 0];
    return defaultLevelsA !== currentLevelsA || defaultLevelsB !== currentLevelsB;
  }, [defaultValue, levels]);

  const [updatePictures] = useUpdatePicturesMutation();

  useEffect(() => {
    onLevelsChange && onLevelsChange(isEdited);
  }, [onLevelsChange, isEdited]);

  const handleChange = useCallback(
    ([min, max]) => {
      const a = Number((255 / (max - min)).toFixed(4));
      const b = Math.round(-a * min);
      const levels = { a, b };
      setLevels(levels);
      setValue([min, max]);
      onChange({ levels });
    },
    [onChange]
  );

  const handleReset = useCallback(() => {
    hasDefaultLevels ? handleChange(toValue({ a, b })) : handleChange([0, 255]);
  }, [handleChange, hasDefaultLevels, a, b]);

  const handleSave = async () => {
    setSaving(true);
    await updatePictures({
      variables: {
        picturesToUpdate: [
          {
            globalId: pictureGlobalId,
            branchGlobalId: pictureId,
            viewSettings: {
              linear: [levels?.a ?? 1, levels?.b ?? 0],
            },
          },
        ],
      },
    });
    setSaving(false);

    refetch();
    onLevelsToggle();
  };

  return (
    <LevelsContainer onClick={e => e.stopPropagation()}>
      <span>{t('components.shadows', 'Shadows')}</span>
      <Slider
        tooltip={{ open: false }}
        value={value[0]}
        id="image-levels"
        max={255}
        min={0}
        onChange={shadows => handleChange([shadows, value[1]])}
        step={1}
        styles={{
          track: { background: theme.teal },
          rail: { background: theme.secondaryBackgroundColor },
        }}
      />

      <span>{t('components.highlights', 'Highlights')}</span>
      <Slider
        tooltip={{ open: false }}
        value={value[1]}
        id="image-levels"
        max={255}
        min={1}
        onChange={highlights => handleChange([value[0], highlights])}
        step={1}
        reverse
        styles={{
          track: { background: theme.secondaryBackgroundColor },
          rail: { background: theme.teal },
        }}
      />

      <LevelsButtonContainer>
        <Button size="small" onClick={handleReset} disabled={!isEdited}>
          {t('table.reset', 'Reset')}
        </Button>
        <div>
          {isEditor && (
            <Button
              size="small"
              type="primary"
              onClick={handleSave}
              loading={saving}
              disabled={!isEdited}
            >
              {t('common.save', 'Save')}
            </Button>
          )}

          <Button size="small" onClick={onLevelsToggle}>
            {t('common.close', 'Close')}
          </Button>
        </div>
      </LevelsButtonContainer>
    </LevelsContainer>
  );
};
